import { Fragment, useEffect, useState, useCallback } from "react";
import { useApiClient } from "../api/useApiClient";
import type { ReviewItem } from "../types/reviewItem";
import { useHasRole } from "../auth/useRoles";
import { ROLE_CONTADURIA } from "../constants/roles";
import { LineasRow, LineasToggle } from "../components/LineasRow";
import { CfeIdentidad } from "../components/CfeIdentidad";
import { TablaToolbar } from "../components/TablaToolbar";
import { Paginador } from "../components/Paginador";
import { ThOrdenable } from "../components/ThOrdenable";
import { abrirEnNuevaPestana } from "../utils/abrirEnNuevaPestana";
import { formatMonto, formatFecha, formatFechaHoraUtc } from "../utils/formato";
import { useTablaBandeja } from "../hooks/useTablaBandeja";
import { useAccion } from "../hooks/useAccion";
import { AvisoError } from "../components/AvisoError";
import { useContadores } from "../contexts/ContadoresContext";

/** Solo Contaduría puede reabrir (ver CLAUDE.md del backend). */
export function RechazadasPage() {
  const api = useApiClient();
  const esContaduria = useHasRole(ROLE_CONTADURIA);
  const { refrescar: refrescarContadores } = useContadores();
  const [items, setItems] = useState<ReviewItem[]>([]);
  const [expandidos, setExpandidos] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  // [SIMPLIFICADO 09/09/2026] Antes eran 7 u 8 columnas (Acciones aparte,
  // solo para Contaduría). Con Documentos+Acciones fusionadas en una sola
  // celda (ver el <td> más abajo) la cantidad de columnas ya no depende del
  // rol — siempre 7.
  const columnas = 7;

  const tabla = useTablaBandeja(items, {
    camposBusqueda: ["proveedor_nombre", "rut_emisor", "nro", "serie", "rejection_reason"],
    // Acá el orden natural es "lo último rechazado primero" — es el dato que
    // importa en esta bandeja, no la fecha del documento.
    ordenInicial: { columna: "reviewed_at", direccion: "desc" },
    camposNumericos: ["monto"],
    claveEstable: "id",
  });

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      // paso no importa mucho acá filtrando por 'rechazadas' — cualquiera
      // de los dos roles puede ver esta lista.
      const reviewItems = await api.listarReviewItems("contaduria", "rechazadas");
      setItems(reviewItems);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const recargarTodo = useCallback(async () => {
    await cargar();
    refrescarContadores();
  }, [cargar, refrescarContadores]);

  const accion = useAccion(recargarTodo);

  // Via `accion.ejecutar`: refresca SIEMPRE y muestra el error sin destruir
  // la tabla. Ver useAccion.ts y el incidente del 10/08/2026.
  async function reabrir(id: string) {
    await accion.ejecutar(() => api.reabrir(id), { descripcion: "reabrir la factura" });
  }

  async function archivar(id: string) {
    await accion.ejecutar(() => api.archivar(id), { descripcion: "archivar la factura" });
  }

  function toggleExpandido(id: string) {
    setExpandidos((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function abrirDocumento(id: string, tipo: "xml" | "pdf") {
    try {
      const url = await api.obtenerBlobSasUrl(id, tipo);
      abrirEnNuevaPestana(url);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(`No se pudo obtener el ${tipo.toUpperCase()}:`, err);
      window.alert(`No se pudo abrir el ${tipo.toUpperCase()}. Ver consola para el detalle.`);
    }
  }

  if (loading) return <p>Cargando...</p>;

  return (
    <div>
      <h1>Rechazadas</h1>

      <TablaToolbar
        busqueda={tabla.busqueda}
        onBuscar={tabla.setBusqueda}
        filtradas={tabla.filtradas}
        totalSinFiltro={items.length}
        placeholder="Buscar por proveedor, RUT, numero de CFE o motivo..."
      />

      <AvisoError mensaje={accion.error} onCerrar={accion.limpiarError} />

      <table className="compact">
        {/* [AJUSTADO 09/09/2026, segunda vuelta] El primer intento (agregar
            colgroup pero mantener "Documentos" y "Acciones" como columnas
            separadas) no alcanzó: sin borde vertical entre celdas, dos
            columnas angostas y pegadas se siguen viendo como una sola
            aunque cada una tenga su ancho — confirmado por Liber mirando la
            pantalla real en el puerto correcto. Pedido explícito: fusionar
            las dos en una, solo en esta página. Ya no hay rama por rol —
            "Documentos" es una sola columna para todos, con Reabrir/Archivar
            adentro cuando corresponde (ver el <td> más abajo). Suma 100%:
            14+17+9+8+19+11+22. */}
        <colgroup>
          <col style={{ width: "14%" }} />
          <col style={{ width: "17%" }} />
          <col style={{ width: "9%" }} />
          <col style={{ width: "8%" }} />
          <col style={{ width: "19%" }} />
          <col style={{ width: "11%" }} />
          <col style={{ width: "22%" }} />
        </colgroup>
        <thead>
          <tr>
            <ThOrdenable columna="nro" orden={tabla.orden} onOrdenar={tabla.alternarOrden}>
              Documento
            </ThOrdenable>
            <ThOrdenable columna="proveedor_nombre" orden={tabla.orden} onOrdenar={tabla.alternarOrden}>
              Proveedor
            </ThOrdenable>
            <ThOrdenable columna="monto" orden={tabla.orden} onOrdenar={tabla.alternarOrden} alineado="derecha">
              Monto
            </ThOrdenable>
            <ThOrdenable columna="fecha_documento" orden={tabla.orden} onOrdenar={tabla.alternarOrden}>
              Fecha doc.
            </ThOrdenable>
            <th>Motivo de rechazo</th>
            <ThOrdenable columna="reviewed_at" orden={tabla.orden} onOrdenar={tabla.alternarOrden}>
              Rechazada el
            </ThOrdenable>
            <th>Documentos</th>
          </tr>
        </thead>
        <tbody>
          {tabla.filas.length === 0 && (
            <tr>
              <td colSpan={columnas} className="empty-row">
                {items.length === 0
                  ? "No hay facturas rechazadas."
                  : "Ninguna factura coincide con la busqueda."}
              </td>
            </tr>
          )}
          {tabla.filas.map((item) => (
            <Fragment key={item.id}>
              <tr>
                <td>
                  <CfeIdentidad
                    tipoCfe={item.tipo_cfe}
                    serie={item.serie}
                    nro={item.nro}
                    rutEmisor={item.rut_emisor}
                  />
                </td>
                <td className="wrap-ok" title={item.proveedor_nombre ?? undefined}>
                  {item.proveedor_nombre}
                </td>
                <td className="mono celda-monto">
                  {formatMonto(item.monto)}
                  <span className="moneda">{item.moneda}</span>
                </td>
                <td className="mono">{formatFecha(item.fecha_documento)}</td>
                <td className="rejection-reason">{item.rejection_reason}</td>
                <td className="mono">{formatFechaHoraUtc(item.reviewed_at)}</td>
                <td className="actions-cell">
                  <button className="btn-ghost" onClick={() => abrirDocumento(item.id, "xml")}>
                    XML
                  </button>
                  {item.pdf_blob_url && (
                    <button className="btn-ghost" onClick={() => abrirDocumento(item.id, "pdf")}>
                      PDF
                    </button>
                  )}
                  <LineasToggle expanded={expandidos.has(item.id)} onToggle={() => toggleExpandido(item.id)} />
                  {/* [FUSIONADO 09/09/2026] Reabrir/Archivar vivían en una
                      columna "Acciones" separada — visualmente indistinguible
                      de esta, sin borde entre las dos. Pedido explícito de
                      Liber: todo junto, en una sola celda. */}
                  {esContaduria && (
                    <>
                      <button disabled={accion.enCurso} onClick={() => reabrir(item.id)}>
                        Reabrir
                      </button>
                      <button className="btn-ghost" disabled={accion.enCurso} onClick={() => archivar(item.id)}>
                        Archivar
                      </button>
                    </>
                  )}
                </td>
              </tr>
              {expandidos.has(item.id) && <LineasRow reviewItemId={item.id} colSpan={columnas} />}
            </Fragment>
          ))}
        </tbody>
      </table>

      <Paginador
        pagina={tabla.pagina}
        totalPaginas={tabla.totalPaginas}
        desde={tabla.desde}
        hasta={tabla.hasta}
        total={tabla.total}
        onIrAPagina={tabla.irAPagina}
      />
    </div>
  );
}
